import { APP_ROLES, type AppRole } from './roles'

/**
 * The dev role picker (adr/012). In dev the API takes the roles in
 * DEV_ROLES_HEADER in place of the token's, so a person assigned only `User`
 * can walk every role's flows. The choice lives in localStorage so it
 * survives reloads.
 *
 * `VITE_DEV_ROLE_SWITCHER` is a build-time value that deploy.yml sets for dev
 * only, so the prod bundle carries the constant `false` and never reads the
 * choice or sends the header. The prod API would ignore it anyway.
 */
export const DEV_ROLE_SWITCHER = import.meta.env.VITE_DEV_ROLE_SWITCHER === 'true'

/** Must match DEV_ROLES_HEADER in api/src/authentication.ts. */
export const DEV_ROLES_HEADER = 'X-Keystone-Dev-Roles'

const KEY = 'keystone:dev-roles'

/** The roles picked, or null to use the token's. */
export function readDevRoles(): AppRole[] | null {
  if (!DEV_ROLE_SWITCHER) return null
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(KEY) ?? 'null')
    if (!Array.isArray(stored)) return null
    const roles = APP_ROLES.filter(r => stored.includes(r))
    return roles.length > 0 ? roles : null
  } catch {
    return null
  }
}

/**
 * Remember the choice and reload. A reload rather than new state, because
 * every cached query and the chat thread were fetched under the old roles.
 */
export function applyDevRoles(roles: AppRole[] | null): void {
  try {
    if (roles && roles.length > 0) localStorage.setItem(KEY, JSON.stringify(roles))
    else localStorage.removeItem(KEY)
  } catch {
    // Nowhere to keep it; the reload falls back to the token's roles.
  }
  window.location.reload()
}
